/**
 * Returns an interface for throttling a function call
 * (similar to mootools' :pause Event pseudo)
 *
 * fire() can be called as many times as needed, but the
 * callback is called when fire() hasn't been called for `delay`
 * milliseconds.
 *
 * calling fireNow() executes callback() immediately and clears the timer.
 */
export function throttler(
   delay: number,
   callback: (...args: any[]) => void
): { fire: (...args: any[]) => void; fireNow: (...args: any[]) => void } {
   let timer: ReturnType<typeof setTimeout>;

   function fireNow(...args: any[]) {
      clear();
      callback(...args);
   }

   function fireLater(...args: any[]) {
      clear();
      timer = setTimeout(() => fireNow(...args), delay);
   }

   function clear() {
      clearTimeout(timer);
   }

   return {
      fire: fireLater,
      fireNow: fireNow,
   };
}

/**
 * Returns an interface for throttling a function call.
 * The rate is limited to once per `delay` milliseconds.
 * The design ensures that calling `fire()` guarantees that `callback` will
 * be called within `delay` milliseconds.
 *
 * `fire()` can be called as many times as needed, but the
 * callback will only be called once per `delay` milliseconds.
 */
export function rateLimiter(
   delay: number,
   callback: (...args: any[]) => void
): (...args: any[]) => void {
   let paused: ReturnType<typeof setTimeout> | null;
   let shouldFire: boolean;
   let lastArgs: any[] = [];

   function fire(...args: any[]) {
      lastArgs = args;
      if (paused) {
         shouldFire = true;
         return;
      } else {
         callback(...args);
         pause();
      }
   }

   function pause() {
      shouldFire = false;
      if (paused) {
         clearTimeout(paused);
      }
      paused = setTimeout(() => {
         paused = null;
         if (shouldFire) {
            fire(...lastArgs);
         }
      }, delay);
   }

   return fire;
}
